import { Icon, Shadow, Text, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Spacer } from 'native-base';
import React, {
  ClassAttributes,
  DragEvent,
  HTMLAttributes,
  ReactNode,
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';

import { allowedExtensions, allowedMimeTypes, maxFileSize } from './constants';

const dragWrapperPadding = Spacing['inline.xxxs'];
const fileFieldStyles = StyleSheet.create({
  dragWrapper: {
    position: 'relative',
    padding: dragWrapperPadding,
  },
  fileInput: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    opacity: 0,
    cursor: 'pointer',
  },
});

export declare namespace FileFieldTypes {
  export type Props = {
    onSelectFile?: (file: File) => void;
    onReset?: () => void;
    onError?: () => void;
    disabled?: boolean;
  };
}

const DragWrapper: React.FC<
  ClassAttributes<HTMLDivElement> &
    HTMLAttributes<HTMLDivElement> & { children: ReactNode | ReactNode[] }
> = ({ children, ...props }) => {
  if (Platform.OS === 'web') {
    return createElement('div', props, children);
  } else {
    return <View>{children}</View>;
  }
};

export const FileField: React.FC<FileFieldTypes.Props> = ({
  onSelectFile,
  onReset,
  onError,
  disabled,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [fileName, setFileName] = useState<string>('');
  const [file, setFile] = useState<File | undefined>();
  const [fileSize, setFileSize] = useState<string>('');

  const validateFile = useCallback(
    (selectedFile: File) => {
      setFileName(selectedFile.name.toUpperCase());

      if (selectedFile.size > maxFileSize) {
        setError('Tamanho máximo excedido.');
        return;
      }

      if (
        !allowedExtensions.exec(selectedFile.name) ||
        !allowedMimeTypes.includes(selectedFile.type)
      ) {
        setError(
          'Tipo de arquivo inválido. Somente planilhas .xls, .xlsx, and .csv são permitidas.'
        );
      }

      setFile(selectedFile);
      setFileSize(Utils.Mask.bytesFormat(selectedFile.size));
      onSelectFile?.(selectedFile);
    },
    [onSelectFile]
  );

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) {
      return;
    }

    e.preventDefault();
    setDragOver(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const currentFile = e.dataTransfer.files[0];
      validateFile(currentFile);
    }
  };

  const dragStyle = dragOver ? { borderStyle: 'solid' } : {};
  const errorStyle = error
    ? { borderStyle: 'solid', borderColor: Color['feedback.error.default'] }
    : {};
  const withFileStyle = file ? { borderColor: 'transparent' } : {};

  const webFileField = useMemo(() => {
    if (Platform.OS === 'web' && !error && !file) {
      return (
        <input
          type="file"
          accept=".xls,.xlsx,.csv"
          style={fileFieldStyles.fileInput}
          disabled={disabled}
          onChange={(e) => {
            if (e.target.files && e.target.files[0]) {
              const currentFile = e.target.files[0];
              validateFile(currentFile);
            }
          }}
        />
      );
    }

    return null;
  }, [disabled, error, file, validateFile]);

  const stackContent = useMemo(() => {
    const handleReset = () => {
      if (disabled) {
        return;
      }

      setError(undefined);
      setFileName('');
      setFile(undefined);
      setFileSize('');
      onReset?.();
    };

    if (dragOver) {
      return (
        <>
          <Text color="brand.tertiary.principal">Solte o arquivo para iniciar o carregamento.</Text>
          <Spacer />
          <Icon
            name="Upload"
            size="sm"
            testId="upload-icon"
            color={Color['brand.tertiary.principal']}
          />
        </>
      );
    }

    if (error) {
      return (
        <>
          <HStack flex={1} alignItems="center" space={Spacing['stack.nano']}>
            <Icon
              name="Warning"
              size="xxs"
              testId="warning-icon"
              color={Color['feedback.error.default']}
            />
            <VStack space={Spacing['stack.quarck']}>
              <Text variant="semibold">{fileName}</Text>
              <Text color="feedback.error.default">{error}</Text>
            </VStack>
            <Spacer />
            <Pressable onPress={handleReset}>
              <Icon
                name="Close"
                size="xxs"
                testId="error-icon"
                color={Color['feedback.error.default']}
              />
            </Pressable>
          </HStack>
        </>
      );
    }

    if (file) {
      return (
        <>
          <HStack flex={1} alignItems="center" space={Spacing['stack.quarck']}>
            <Icon
              name="Spreadsheet"
              size="sm"
              testId="spreadsheet-icon"
              color={Color['brand.tertiary.principal']}
            />
            <VStack space={Spacing['stack.quarck']}>
              <Text variant="bold">{fileName}</Text>
              <Text fontSize={Typography['fontsize.xs']} color="neutral.medium">
                Tamanho do arquivo: {fileSize}
              </Text>
            </VStack>
            <Spacer />
            <Pressable onPress={handleReset}>
              <Icon name="Trash" size="xs" testId="error-icon" color={Color['neutral.medium']} />
            </Pressable>
          </HStack>
        </>
      );
    }

    return (
      <>
        <VStack space={Spacing['stack.quarck']}>
          <Text fontSize={Typography['fontsize.md']}>Selecione ou arraste a planilha</Text>
          <Text fontSize={Typography['fontsize.xs']} color="neutral.medium">
            XLS ou XLSX (max. 5MB)
          </Text>
        </VStack>
        <Spacer />
        <Icon
          name="Upload"
          size="sm"
          testId="upload-icon"
          color={Color['brand.tertiary.principal']}
        />
      </>
    );
  }, [disabled, dragOver, error, file, fileName, fileSize, onReset]);

  useEffect(() => {
    if (error) {
      onError?.();
      onReset?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Shadow
      testId="export-shadow"
      level={2}
      borderRadius={BoxRadius.sm}
      borderWidth={1}
      borderColor={Color['brand.tertiary.principal']}
      borderStyle="dashed"
      {...dragStyle}
      {...errorStyle}
      {...withFileStyle}>
      <DragWrapper
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={fileFieldStyles.dragWrapper}>
        <HStack alignItems="center">{stackContent}</HStack>
        {webFileField}
      </DragWrapper>
    </Shadow>
  );
};
